<template>
  <v-dialog
    content-class='suppress-issue-popup'
    :value='visiblePopup'
    max-width='520px'
    :persistent='loading'
    @input='onDialogInput'
    @click:outside='close'
    @keydown.esc='close'
  >
    <v-card class='suppress-issue-popup__card'>
      <div class='suppress-issue-popup__header'>
        <div class='suppress-issue-popup__title'>
          {{ isEdit ? $t('suppression.dialog.title.edit') : $tc('suppression.dialog.title', issuesCount, { count: issuesCount }) }}
        </div>
        <button type='button' class='suppress-issue-popup__close' :aria-label='$t("newProject.esc")' @click='close'>
          <span class='material-icons-outlined' aria-hidden='true'>close</span>
        </button>
      </div>

      <v-form ref='form' v-model='valid' @submit.prevent='submit'>
        <fieldset class='suppress-issue-popup__field'>
          <legend class='suppress-issue-popup__label'>{{ $t('suppression.field.type') }} *</legend>
          <v-radio-group v-model='form.type' :rules='[rules.required]' row dense hide-details='auto'>
            <v-radio :label='$t("suppression.type.permanent")' :value='types.PERMANENT' />
            <v-radio :label='$t("suppression.type.temporary")' :value='types.TEMPORARY' />
          </v-radio-group>
        </fieldset>

        <div class='suppress-issue-popup__field'>
          <v-select
            v-model='form.reason'
            :items='reasonItems'
            :label='`${$t("suppression.field.reason")} *`'
            :rules='[rules.required]'
            outlined
            dense
            hide-details='auto'
          />
        </div>

        <div class='suppress-issue-popup__field' v-if='form.reason === reasons.OTHER'>
          <v-text-field
            v-model='form.reasonOther'
            :label='`${$t("suppression.field.reason.other")} *`'
            :rules='[rules.notBlank, rules.maxLength(maxReasonOther)]'
            :counter='maxReasonOther'
            outlined
            dense
          />
        </div>

        <div class='suppress-issue-popup__field'>
          <v-textarea
            v-model='form.justification'
            class='suppress-issue-popup__justification'
            :label='`${$t("suppression.field.justification")} *`'
            :rules='[rules.notBlank, rules.maxLength(maxJustification)]'
            :counter='maxJustification'
            rows='1'
            auto-grow
            outlined
            dense
          />
        </div>

        <div class='suppress-issue-popup__field' v-if='form.type === types.TEMPORARY'>
          <date-picker
            :title='`${$t("suppression.field.expires")} *`'
            :input-date='form.expiresOn'
            :is-date-allowed='isExpiryAllowed'
            @changed='date => (form.expiresOn = date)'
          />
          <div class='suppress-issue-popup__hint'>{{ expiryHint }}</div>
        </div>

        <div class='suppress-issue-popup__meta'>
          {{ $t('suppression.dialog.meta', { name: actorName, date: today }) }}
        </div>

        <div class='suppress-issue-popup__actions'>
          <v-btn small depressed class='btn-secondary' type='button' @click.stop='close'>
            {{ $t('suppression.dialog.cancel') }}
          </v-btn>
          <v-btn small depressed class='btn-primary' type='submit' :disabled='!canSubmit' :loading='loading'>
            {{ isEdit ? $t('suppression.dialog.save') : $t('suppression.dialog.submit') }}
          </v-btn>
        </div>
      </v-form>
    </v-card>
  </v-dialog>
</template>

<script>
import moment from 'moment';
import { mapGetters } from 'vuex';

import DatePicker from '@/components/common/DatePicker.vue';
import disableOuterScrollOnPopupVisible from '@/mixins/disableOuterScrollOnPopupVisible';
import {
  MAX_JUSTIFICATION_LENGTH,
  MAX_REASON_OTHER_LENGTH,
  MAX_TEMPORARY_EXCEPTION_DAYS,
  SUPPRESSION_REASONS,
  SUPPRESSION_TYPES,
} from '@/constants/suppression';
import { endOfDayIso, formatSuppressionDate } from '@/utils/suppression';

const DATE_FORMAT = 'YYYY-MM-DD';
const DEFAULT_EXCEPTION_DAYS = 30;

function emptyForm() {
  return {
    type: null,
    reason: null,
    reasonOther: '',
    justification: '',
    expiresOn: moment().add(DEFAULT_EXCEPTION_DAYS, 'days').format(DATE_FORMAT),
  };
}

export default {
  name: 'SuppressIssuePopup',
  components: { DatePicker },
  mixins: [disableOuterScrollOnPopupVisible],
  props: {
    visiblePopup: {
      type: Boolean,
      required: true,
    },
    /** Number of findings the action applies to (shown in the title). */
    issuesCount: {
      type: Number,
      default: 1,
    },
    /** Current suppression when editing / extending. */
    initial: {
      type: Object,
      default: null,
    },
    loading: {
      type: Boolean,
      default: false,
    },
  },
  data() {
    return {
      valid: false,
      form: emptyForm(),
      types: SUPPRESSION_TYPES,
      reasons: SUPPRESSION_REASONS,
      maxJustification: MAX_JUSTIFICATION_LENGTH,
      maxReasonOther: MAX_REASON_OTHER_LENGTH,
      rules: {
        required: v => !!v || this.$t('suppression.validation.required'),
        notBlank: v => (!!v && v.trim().length > 0) || this.$t('suppression.validation.required'),
        maxLength: max => v => !v || v.length <= max || this.$t('suppression.validation.max.length', { max }),
      },
    };
  },
  watch: {
    visiblePopup: {
      immediate: true,
      handler(visible) {
        if (visible) {
          this.resetForm();
        }
      },
    },
  },
  computed: {
    ...mapGetters(['profile']),
    isEdit() {
      return !!this.initial;
    },
    reasonItems() {
      return Object.values(SUPPRESSION_REASONS).map(value => ({ value, text: this.$t(`suppression.reason.${value}`) }));
    },
    actorName() {
      const name = [this.profile?.firstName, this.profile?.lastName].filter(Boolean).join(' ');
      return name || this.profile?.email || this.$t('suppression.dialog.you');
    },
    today() {
      return formatSuppressionDate(new Date());
    },
    expiryHint() {
      const days = moment(this.form.expiresOn, DATE_FORMAT).diff(moment().startOf('day'), 'days');
      return this.$tc('suppression.dialog.expiry.hint', days, { days, max: MAX_TEMPORARY_EXCEPTION_DAYS });
    },
    canSubmit() {
      if (!this.valid || !this.form.type || !this.form.reason) {
        return false;
      }
      return this.form.type !== SUPPRESSION_TYPES.TEMPORARY || this.isExpiryAllowed(this.form.expiresOn);
    },
  },
  methods: {
    resetForm() {
      const form = emptyForm();
      if (this.initial) {
        form.type = this.initial.type;
        form.reason = this.initial.reason;
        form.reasonOther = this.initial.reasonOther || '';
        form.justification = this.initial.justification || '';
        if (this.initial.expiresAt) {
          form.expiresOn = moment(this.initial.expiresAt).format(DATE_FORMAT);
        }
      }
      this.form = form;
      this.$nextTick(() => this.$refs.form && this.$refs.form.resetValidation());
    },
    /** Tomorrow up to the maximum exception length. */
    isExpiryAllowed(date) {
      const day = moment(date, DATE_FORMAT);
      const today = moment().startOf('day');
      return day.isAfter(today) && day.diff(today, 'days') <= MAX_TEMPORARY_EXCEPTION_DAYS;
    },
    submit() {
      if (!this.canSubmit || this.loading) {
        return;
      }
      const temporary = this.form.type === SUPPRESSION_TYPES.TEMPORARY;
      this.$emit('submit', {
        type: this.form.type,
        reason: this.form.reason,
        reasonOther: this.form.reason === SUPPRESSION_REASONS.OTHER ? this.form.reasonOther.trim() : undefined,
        justification: this.form.justification.trim(),
        expiresAt: temporary ? endOfDayIso(this.form.expiresOn) : undefined,
      });
    },
    close() {
      if (!this.loading) {
        this.$emit('closed');
      }
    },
    /** Vuetify closes the dialog itself (outside click, Esc); keep the parent's flag in sync. */
    onDialogInput(open) {
      if (!open) {
        this.close();
      }
    },
  },
};
</script>

<style lang='scss'>
@import '~@/assets/styles/design-system';

.suppress-issue-popup {
  .suppress-issue-popup__card {
    padding: $space-8;
  }

  .suppress-issue-popup__header {
    display: flex;
    justify-content: space-between;
    align-items: flex-start;
    margin-bottom: $space-6;
  }

  .suppress-issue-popup__title {
    color: var(--text-primary);
    font-size: $font-size-lg;
    font-weight: 600;
    letter-spacing: 1.12px;
    text-transform: uppercase;
  }

  // Pinned to the card corner rather than the padded header row.
  .suppress-issue-popup__close {
    position: absolute;
    top: $space-4;
    right: $space-4;
    display: flex;
    padding: 0;
    color: var(--text-secondary);
    background: none;
    border: none;
    cursor: pointer;

    &:hover,
    &:focus-visible {
      color: var(--brand-primary);
    }
  }

  .suppress-issue-popup__field {
    border: none;
    padding: 0;
    margin: 0 0 $space-4;
  }

  // Global form styles pin dense outlined fields to 32px with 1px padding (sized for one-line inputs),
  // which breaks Vuetify's textarea offsets. Let this box grow with the text and inset it like the inputs.
  .suppress-issue-popup__justification.v-textarea.v-input--dense > .v-input__control > .v-input__slot {
    height: auto;

    .v-text-field__slot {
      margin-right: 0;
    }

    // 3px + 24px line + 3px, plus the slot's 1px padding, centers one line in the 32px box.
    textarea {
      min-height: 0;
      margin: rem(3) 0;
      padding: 0 $space-3;
      line-height: rem(24);
    }
  }

  .suppress-issue-popup__label {
    color: var(--text-secondary);
    font-size: $font-size-xs;
    letter-spacing: 0.8px;
    text-transform: uppercase;
  }

  .suppress-issue-popup__hint,
  .suppress-issue-popup__meta {
    color: var(--text-secondary);
    font-size: $font-size-sm;
  }

  .suppress-issue-popup__actions {
    display: flex;
    justify-content: flex-end;
    gap: $space-2;
    margin-top: $space-6;

    .v-btn {
      height: $btn-height-md;
      border-radius: $radius-sm;
    }
  }
}
</style>
